import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { useBillingState } from '@/modules/billing/hooks/useBillingState';

import { type InvoiceTableRow, parseInvoices } from './utils';

export const InvoiceDetailsTable = () => {
  const { t } = useTranslation();
  const { currentSubscription, upcomingInvoice } = useBilling();
  const billingState = useBillingState();

  const data = useMemo<InvoiceTableRow[]>(() => {
    if (!upcomingInvoice?.lines) {
      return [];
    }
    return parseInvoices(
      upcomingInvoice.lines,
      billingState?.plan_name ?? '',
      upcomingInvoice?.currency ?? 'USD'
    );
  }, [upcomingInvoice, billingState?.plan_name]);

  if (!currentSubscription || !upcomingInvoice) {
    return null;
  }

  if (!data.length || upcomingInvoice?.total === null) {
    return null;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="px-4">{t('billing.table.item')}</TableHead>
          <TableHead className="px-4">{t('billing.table.count')}</TableHead>
          <TableHead className="px-4 text-right">{t('billing.table.unit')}</TableHead>
          <TableHead className="px-4 text-right">{t('billing.table.price')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="px-4">{row.item}</TableCell>
            <TableCell className="px-4">{row.count}</TableCell>
            <TableCell className="px-4 text-right font-mono text-foreground-muted">
              {row.unit_price}
            </TableCell>
            <TableCell className="px-4 text-right font-mono text-foreground-muted">
              {row.price}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
